import React, { useState, useEffect } from "react";
import YouTube from "react-youtube";
import Modal from "./Model"; // Make sure this path is correct
import "./course.css";
import { useTranslation } from "react-i18next";

const API_KEY = "AIzaSyCexWa7pemo9xi-qXkTRv1lyH0FPnaqrdA";
const PLAYLIST_ID = "PLtztnlFXOSfydSp27GrEjxVTEMTr8eP-j";

function Learning() {
  const { t } = useTranslation();
  const [videos, setVideos] = useState([]);
  const [shorts, setShorts] = useState([]);
  const [selectedVideo, setSelectedVideo] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const [isMobile, setIsMobile] = useState(false);
  const [accordionState, setAccordionState] = useState({
    videos: false,
    shorts: false
  });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Check if device is mobile
  useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth <= 768);
    };

    checkMobile();
    window.addEventListener("resize", checkMobile);
    return () => window.removeEventListener("resize", checkMobile);
  }, []);

  // Fetch videos
  useEffect(() => {
    const fetchVideos = async () => {
      try {
        setLoading(true);
        const videoList = await fetchVideoList(PLAYLIST_ID, API_KEY);

        if (videoList.length === 0) {
          throw new Error("No videos found in the playlist");
        }

        const shortsList = videoList.filter(video => video.isShort);
        const regularVideos = videoList.filter(video => !video.isShort);

        setVideos(regularVideos);
        setShorts(shortsList);
      } catch (err) {
        console.error("Error fetching videos:", err);
        setError(err.message);
      } finally {
        setLoading(false);
      }
    };

    fetchVideos();
  }, []);

  const handleVideoClick = (videoId) => {
    setSelectedVideo(videoId);
    setShowModal(true);

    if (isMobile) {
      document.body.style.overflow = "hidden";
    }
  };

  const handleCloseModal = () => {
    setShowModal(false);
    setSelectedVideo(null);
    document.body.style.overflow = "unset";
  };

  const toggleAccordion = (section) => {
    setAccordionState(prev => ({
      ...prev,
      [section]: !prev[section]
    }));
  };

  const videoOptions = {
    height: isMobile ? "250" : "390",
    width: isMobile ? "100%" : "640",
    playerVars: {
      autoplay: 1,
      modestbranding: 1,
      rel: 0
    },
  };

  if (loading) {
    return <div className="loading-message">{t("Loading videos...")}</div>;
  }

  if (error) {
    return <div className="error-message">{t("Error loading videos:")} {error}</div>;
  }

  return (
    <div className="learning-container border">
      <h1 className="learning-heading">{t("Course Playlist")}</h1>

      {/* Modal with mobile fullscreen support */}
      <Modal show={showModal} onClose={handleCloseModal} className={isMobile ? "mobile-fullscreen-modal" : ""}>
        {isMobile && (
          <button className="mobile-close-btn" onClick={handleCloseModal}>
            ×
          </button>
        )}
        {selectedVideo && (
          <YouTube videoId={selectedVideo} opts={videoOptions} />
        )}
      </Modal>

      {/* Desktop Layout */}
      {!isMobile && (
        <div className="sections-wrapper">
          {/* Regular Videos Section */}
          <div className="section-container videos-section">
            <h2 className="section-heading">{t("Course Videos")} ({videos.length})</h2>
            <div className="video-list">
              {videos.map((video) => (
                <div
                  className="video-item"
                  key={video.id}
                  onClick={() => handleVideoClick(video.id)}
                >
                  <div className="thumbnail-container">
                    <img src={video.thumbnail} alt={video.title} />
                  </div>
                  <h3>{video.title}</h3>
                </div>
              ))}
            </div>
          </div>

          {/* Shorts Section */}
          <div className="section-container shorts-section">
            <h2 className="section-heading">{t("YouTube Shorts")} ({shorts.length})</h2>
            <div className="shorts-list">
              {shorts.map((short) => (
                <div
                  className="short-item"
                  key={short.id}
                  onClick={() => handleVideoClick(short.id)}
                >
                  <div className="short-thumbnail-container">
                    <img src={short.thumbnail} alt={short.title} />
                  </div>
                  <h3>{short.title}</h3>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* Mobile Accordion Layout */}
      {isMobile && (
        <div className="mobile-accordion">
          {/* Course Videos Accordion */}
          <button
            className={`accordion-header ${accordionState.videos ? "active" : ""}`}
            onClick={() => toggleAccordion("videos")}
          >
            <span>{t("Course Videos")} ({videos.length})</span>
            <span className="accordion-icon">
              {accordionState.videos ? "▲" : "▼"}
            </span>
          </button>
          <div className={`accordion-content ${accordionState.videos ? "active" : ""}`}>
            <div className="mobile-video-grid">
              {videos.map((video) => (
                <div
                  className="video-item"
                  key={video.id}
                  onClick={() => handleVideoClick(video.id)}
                >
                  <div className="thumbnail-container">
                    <img src={video.thumbnail} alt={video.title} />
                  </div>
                  <h3>{video.title}</h3>
                </div>
              ))}
            </div>
          </div>

          {/* YouTube Shorts Accordion */}
          <button
            className={`accordion-header ${accordionState.shorts ? "active" : ""}`}
            onClick={() => toggleAccordion("shorts")}
          >
            <span>{t("YouTube Shorts")} ({shorts.length})</span>
            <span className="accordion-icon">
              {accordionState.shorts ? "▲" : "▼"}
            </span>
          </button>
          <div className={`accordion-content ${accordionState.shorts ? "active" : ""}`}>
            <div className="mobile-shorts-grid">
              {shorts.map((short) => (
                <div
                  className="short-item"
                  key={short.id}
                  onClick={() => handleVideoClick(short.id)}
                >
                  <div className="short-thumbnail-container">
                    <img src={short.thumbnail} alt={short.title} />
                  </div>
                  <h3>{short.title}</h3>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

const fetchVideoList = async (playlistId, apiKey) => {
  try {
    // First, get the playlist items
    const playlistUrl = `https://www.googleapis.com/youtube/v3/playlistItems?part=snippet&playlistId=${playlistId}&key=${apiKey}&maxResults=50`;
    const playlistResponse = await fetch(playlistUrl);

    if (!playlistResponse.ok) {
      throw new Error(`YouTube API error: ${playlistResponse.status}`);
    }

    const playlistData = await playlistResponse.json();

    if (!playlistData.items || playlistData.items.length === 0) {
      return [];
    }

    // Extract video IDs
    const videoIdsList = playlistData.items.map(item => item.snippet.resourceId.videoId);

    // Get detailed video information including duration
    const videosUrl = `https://www.googleapis.com/youtube/v3/videos?part=snippet,contentDetails&id=${videoIdsList.join(",")}&key=${apiKey}`;
    const videosResponse = await fetch(videosUrl);

    if (!videosResponse.ok) {
      throw new Error(`YouTube API error: ${videosResponse.status}`);
    }

    const videosData = await videosResponse.json();

    // Parse duration and create video list
    return videosData.items.map((item) => {
      const duration = parseISO8601Duration(item.contentDetails.duration);
      return {
        id: item.id,
        title: item.snippet.title,
        description: item.snippet.description,
        thumbnail: item.snippet.thumbnails.medium?.url || item.snippet.thumbnails.default.url,
        duration: duration,
        isShort: duration <= 120 // Videos 120 seconds or less are considered Shorts
      };
    });

  } catch (error) {
    console.error("Error fetching video list:", error);
    throw error;
  }
};

// Helper function to parse ISO 8601 duration format (PT1M30S -> 90 seconds)
const parseISO8601Duration = (duration) => {
  const match = duration.match(/PT(?:(\d+)H)?(?:(\d+)M)?(?:(\d+)S)?/);
  const hours = parseInt(match[1] || 0);
  const minutes = parseInt(match[2] || 0);
  const seconds = parseInt(match[3] || 0);
  return hours * 3600 + minutes * 60 + seconds;
};

export default Learning;